import React, { useCallback, useState } from 'react';
import { api } from '../utils/api';
import { toEasternDateShort } from '../utils/time';
import { useAutoRefresh } from '../../utils/useAutoRefresh';
import { useListFilter } from '../hooks/useListFilter';
import Tabs from '../components/Tabs';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';

const TABS = [['all', 'All messages'], ['pending', 'Pending'], ['replied', 'Replied'], ['escalated', 'Escalated'], ['lead', 'Lead']];

const statusOf = (m) => m.status;
const searchFields = (m) => [m.sender_name, m.content, m.classification, m.reply_content];
const cell = { maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export default function MessagesPage() {
  const [showToast, toastNode] = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const { sort, onSort, apply } = useSort('created_at', 'desc');

  const load = useCallback(() =>
    api.getMessages()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => showToast('Could not load messages', 'error'))
      .finally(() => setLoading(false)), [showToast]);

  // New inbox messages arrive on the agent's poll cycle, so keep the list fresh while the tab is open.
  useAutoRefresh(load, 30000);

  const handleFetchNow = async () => {
    setFetching(true);
    try {
      await api.fetchMessages();
      showToast('Fetch started. The list refreshes in a few seconds.');
      setTimeout(() => load(), 5000);
    } catch {
      showToast('Could not start the message fetch', 'error');
    } finally {
      setFetching(false);
    }
  };

  const { counts, filtered } = useListFilter(items, { statusOf, searchFields, tab, q });
  const sorted = apply(filtered, (m, col) => m[col] || '');
  const { pageRows, pagerProps } = usePager(sorted, [tab, q, sort.col, sort.dir]);

  return (
    <>
      <PageHeader
        title="Messages"
        subtitle="Facebook inbox messages and how the agent handled each one."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={(
          <>
            <button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>
            <button type="button" className="ops-btn ops-btn--primary" onClick={handleFetchNow} disabled={fetching}>
              <Icon name="download" /> {fetching ? 'Fetching...' : 'Fetch now'}
            </button>
          </>
        )}
      />

      {!loading && items.length > 0 && (
        <div className="ops-kpis ops-kpis--4">
          <StatCard label="Total messages" value={counts.all} />
          <StatCard label="Pending" value={counts.pending || 0} tone={counts.pending > 0 ? 'amber' : undefined} />
          <StatCard label="Escalated" value={counts.escalated || 0} tone={counts.escalated > 0 ? 'red' : undefined} />
          <StatCard label="Replied" value={counts.replied || 0} tone="green" />
        </div>
      )}

      <Panel flush
        title={<Tabs tabs={TABS} active={tab} counts={counts} onChange={setTab} />}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search messages" aria-label="Search messages" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="messages" /> : null}>
        {loading ? <Skeleton rows={6} />
          : items.length === 0 ? <Empty title="No messages yet">Use Fetch now to pull the latest inbox messages from the Page.</Empty>
            : sorted.length === 0 ? <Empty title="No matching messages">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="created_at" label="Date" sort={sort} onSort={onSort} />
                      <SortTh col="sender_name" label="From" sort={sort} onSort={onSort} />
                      <th>Message</th>
                      <th>Classification</th>
                      <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                      <th>Reply sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(m => (
                      <React.Fragment key={m.message_id}>
                        <tr className={`is-clickable${expanded === m.message_id ? ' is-selected' : ''}`}
                          onClick={() => setExpanded(expanded === m.message_id ? null : m.message_id)}>
                          <td className="mono muted">{toEasternDateShort(m.created_at)}</td>
                          <td className="ops-cell-main">{m.sender_name || 'Unknown'}</td>
                          <td className="muted" style={cell}>{m.content}</td>
                          <td>{m.classification ? <span className="ops-pill ops-pill--neutral">{m.classification}</span> : null}</td>
                          <td><StatusPill status={m.status} /></td>
                          <td style={cell}>{m.reply_content || <span className="muted">No reply</span>}</td>
                        </tr>
                        {expanded === m.message_id && (
                          <tr>
                            <td colSpan={6} style={{ background: 'var(--surface-2)', padding: '16px 18px' }}>
                              <dl className="ops-dl">
                                <div><dt>Message</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{m.content}</dd></div>
                                <div><dt>Reply</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{m.reply_content || 'No reply was sent.'}</dd></div>
                              </dl>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
